/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyFulfillmentLineItemKey,
  ShopifyFulfillmentLineItemToReverseFulfillmentOrderLineItemKey,
  ShopifyReverseDeliveryLineItemKey,
  ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderDispositionKey,
  ShopifyReverseFulfillmentOrderDispositionToReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToFulfillmentLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseDeliveryLineItemsKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderDispositionsKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderLineItemToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReverseFulfillmentOrderLineItemKey,
  "2024-07": {
    name: "Shopify Reverse Fulfillment Order Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Dispositions",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderDispositionsKey,
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderDispositionKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderDispositionToReverseFulfillmentOrderLineItemKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "dispositions",
            graphQLFieldKey: "dispositions",
            graphQLSelection: `
          id
          quantity
          type
          location {
            id
          }
          `,
            selectForHasMany: true,
            requiredScopes: ["read_locations"],
          },
        },
      },
      {
        name: "Reverse Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "reverse_fulfillment_order_id",
            graphQLFieldKey: "reverseFulfillmentOrderId",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderKey,
        },
      },
      {
        name: "Reverse Delivery Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseFulfillmentOrderLineItemToReverseDeliveryLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyReverseDeliveryLineItemKey,
          inverseFieldKey: ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderLineItemKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderLineItemToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "return.reverseFulfillmentOrders",
      resource: "reverseFulfillmentOrder.lineItems",
      additionalFields: ["reverseFulfillmentOrderId:id"],
    },
  },
  "2025-04": {
    fields: [
      {
        name: "Fulfillment Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseFulfillmentOrderLineItemToFulfillmentLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentLineItem",
            graphQLSelection: `id`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentLineItemKey,
          inverseFieldKey: ShopifyFulfillmentLineItemToReverseFulfillmentOrderLineItemKey,
        },
      },
    ],
  },
};

export const ShopifyReverseFulfillmentOrderLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
